/**
 * Firestore 書き込みサーキットブレーカー（キルスイッチ A）。
 *
 * 無限ループ検知と強制停止。
 * - 10 秒のスライディングウィンドウで書き込み回数を計測
 * - 閾値超過で throw + console.error + Toast 警告
 * - 無限ループが起きても Firestore への書き込みを物理的に遮断する
 *
 * 仕様書: docs/02_work/done/verification_log.md（2026-04-24 無限ループ事件の再発防止）
 */
import {
  addDoc as fsAddDoc,
  deleteDoc as fsDeleteDoc,
  setDoc as fsSetDoc,
  updateDoc as fsUpdateDoc,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type PartialWithFieldValue,
  type SetOptions,
  type UpdateData,
  type WithFieldValue,
} from 'firebase/firestore';

import { toast } from '@/components/ui/toast';

// ─────────────────────────────────────────────
// 設定
// ─────────────────────────────────────────────
const WINDOW_MS = 10_000;
const MAX_WRITES_PER_WINDOW = 30;

// ─────────────────────────────────────────────
// 状態
// ─────────────────────────────────────────────
const writeTimestamps: number[] = [];
let tripped = false;
let toastShown = false;

// ─────────────────────────────────────────────
// 回路判定
// ─────────────────────────────────────────────
function assertBudget(context: string): void {
  if (tripped) {
    throw new Error(
      `[safeWrite] Circuit breaker tripped (context: ${context}). Reload page to reset.`,
    );
  }

  const now = Date.now();
  while (writeTimestamps.length > 0 && writeTimestamps[0] < now - WINDOW_MS) {
    writeTimestamps.shift();
  }

  if (writeTimestamps.length >= MAX_WRITES_PER_WINDOW) {
    tripped = true;
    const msg = `[safeWrite] CIRCUIT BREAKER: ${writeTimestamps.length} writes in last ${WINDOW_MS}ms. Blocking to prevent infinite loop. context=${context}`;
    // eslint-disable-next-line no-console
    console.error(msg);
    if (!toastShown) {
      toastShown = true;
      toast.error(
        `書き込みループを検出したため Firestore 書き込みを一時停止しました。ページを再読込してください。(${context})`,
      );
    }
    throw new Error(msg);
  }

  writeTimestamps.push(now);
}

// ─────────────────────────────────────────────
// ラッパー（firebase/firestore と同じシグネチャ）
// ─────────────────────────────────────────────
function refPath(
  ref: DocumentReference<DocumentData, DocumentData> | CollectionReference<DocumentData, DocumentData>,
): string {
  return ref.path;
}

export function safeUpdateDoc<T = DocumentData>(
  ref: DocumentReference<T>,
  data: UpdateData<T>,
): Promise<void> {
  assertBudget(`updateDoc(${(ref as DocumentReference<DocumentData>).path})`);
  return fsUpdateDoc(
    ref as DocumentReference<DocumentData>,
    data as UpdateData<DocumentData>,
  );
}

export function safeSetDoc<T = DocumentData>(
  ref: DocumentReference<T>,
  data: WithFieldValue<T>,
): Promise<void>;
export function safeSetDoc<T = DocumentData>(
  ref: DocumentReference<T>,
  data: PartialWithFieldValue<T>,
  options: SetOptions,
): Promise<void>;
export function safeSetDoc(
  ref: DocumentReference,
  data: unknown,
  options?: SetOptions,
): Promise<void> {
  assertBudget(`setDoc(${refPath(ref)})`);
  if (options) {
    return fsSetDoc(ref as DocumentReference<DocumentData>, data as PartialWithFieldValue<DocumentData>, options);
  }
  return fsSetDoc(ref as DocumentReference<DocumentData>, data as WithFieldValue<DocumentData>);
}

export function safeAddDoc<T = DocumentData>(
  ref: CollectionReference<T>,
  data: WithFieldValue<T>,
): Promise<DocumentReference<T>> {
  assertBudget(`addDoc(${(ref as CollectionReference<DocumentData>).path})`);
  return fsAddDoc(ref, data);
}

export function safeDeleteDoc(ref: DocumentReference): Promise<void> {
  assertBudget(`deleteDoc(${refPath(ref)})`);
  return fsDeleteDoc(ref);
}

// ─────────────────────────────────────────────
// テスト / デバッグ用
// ─────────────────────────────────────────────
export function _resetCircuitBreakerForTest(): void {
  writeTimestamps.length = 0;
  tripped = false;
  toastShown = false;
}
